import { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { compress, decompress } from 'lz-string'
import { STALE_TIME } from 'utils/constants'

const cacheTime = 1000 * 60 * 60 * 24 * 7 // 7 days

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME.default,
      cacheTime,
    },
  },
})

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  // production build will use lz-string to compress data before saving to local storage
  ...(process.env.NODE_ENV === 'development'
    ? {}
    : {
        serialize: (data) => compress(JSON.stringify(data)),
        deserialize: (data) => JSON.parse(decompress(data)),
      }),
})

interface Props {
  children: React.ReactNode
}

const QueryProvider = ({ children }: Props) => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      buster: process.env.REACT_APP_VERSION,
      maxAge: cacheTime,
    }}
  >
    {children}
    <ReactQueryDevtools initialIsOpen={false} position="top-left" />
  </PersistQueryClientProvider>
)

export default QueryProvider
